import appLogger from '@/utils/logger';
import axios from 'axios';

// Isolated axios instance for direct-to-storage uploads.
// The global axios instance has its Authorization header mutated by the token
// refresh interceptor (utils/axios.ts), which conflicts with presigned URLs.
// axios.create() shares headers.common by reference with axios.defaults, so
// mutations to axios.defaults.headers.common['Authorization'] leak into this
// instance. The interceptor below strips any inherited auth headers before
// each storage request to prevent presigned URL signature conflicts.
const storageAxios = axios.create();
storageAxios.interceptors.request.use((config) => {
  delete config.headers['Authorization'];
  return config;
});

import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';
import { EmailRequestIntensity, IFile, ILink, PersonaAdvancedSettings, BotConfigPayload } from '@/types';
import { apiInstance, handleShowError } from '@/utils';
import { askIOTApiFetch } from '@/utils/fetch-API';
let cachedProfileAvatarUrl: string | null | undefined;
let profileAvatarRequest: Promise<string | null> | null = null;

export const getProfileAvatar = async (): Promise<string | null> => {
  if (cachedProfileAvatarUrl !== undefined) {
    return cachedProfileAvatarUrl;
  }

  if (profileAvatarRequest) {
    return await profileAvatarRequest;
  }

  const request = (async (): Promise<string | null> => {
    try {
      const response = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/users/profile-pic`);
      cachedProfileAvatarUrl = response?.profilePicUrl ?? null;
      return cachedProfileAvatarUrl ?? null;
    } catch {
      return null;
    } finally {
      profileAvatarRequest = null;
    }
  })();
  profileAvatarRequest = request;

  return await request;
};

function replaceSpaceWithHyphen(inputString: string) {
  // Use the replace method with a regular expression to replace spaces with hyphens
  const resultString = inputString.replace(/ /g, '-');
  return resultString;
}

// export const checkBotStatus = async () => {
//   try {
//     const res = askIOTApiFetch(`${getPublicApiBaseUrl()}/private/chatbot/duplicate`);
//     // ********** //
//     return res;
//   } catch {
//     return null;
//   }
// };

export const getBotData = async (token: string) => {
  try {
    const res = await apiInstance({
      method: 'GET',
      // GH#6239: authenticated and workspace-scoped, so the response carries
      // `allowed_countries` and is not subject to the visitor geo gate. See
      // API_ENDPOINTS.getBotData for why the public endpoint cannot be used here.
      url: API_ENDPOINTS.getBotData(token),
    });

    return res.data;
  } catch {
    return null;
  }
};

export const getOfferData = async () => {
  try {
    const res = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/offers`);
    return res;
  } catch {
    return null;
  }
};

export const getOfferDataByDate = async (value: number | string) => {
  try {
    const res = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/offers?days=${value}`);
    return res;
  } catch {
    return null;
  }
};

export const uploadFileCustomize = async (file: File, type: string): Promise<{ uploadedUri: string }> => {
  const { uri, uploadUrl } = await askIOTApiFetch(
    `${getPublicApiBaseUrl()}/private/chatbot/sas-token`,
    { name: replaceSpaceWithHyphen(file.name), type },
    'POST',
  );
  const uploadedUri = await uploadToStorage(uri, uploadUrl, file);
  return { uploadedUri };
};

export const uploadFile = async (file: File, type: string) => {
  const { uri, uploadUrl } = await askIOTApiFetch(
    `${getPublicApiBaseUrl()}/private/chatbot/sas-token`,
    { name: replaceSpaceWithHyphen(file.name), type },
    'POST',
  );
  const uploadedUri = await uploadToStorage(uri, uploadUrl, file);
  const data = await updateProfilePic(uploadedUri);
  return { data };
};

/**
 * Turn whatever a failed request rejected with into a sentence a user can act on.
 *
 * askIOTApiFetch rejects with the raw Response on a non-2xx, so the server's own message
 * lives in an unread body. Everything else (network failure, CSP block, abort) arrives as
 * an Error. #5012: this exists because three stacked `catch {}` blocks used to discard all
 * of it and the UI guessed "the file already exists" for every possible cause.
 */
export const describeApiError = async (error: unknown, fallback: string): Promise<string> => {
  if (typeof Response !== 'undefined' && error instanceof Response) {
    try {
      const body = await error.clone().json();
      const message = body?.error || body?.message;
      if (typeof message === 'string' && message.trim()) return message;
    } catch {
      try {
        const text = (await error.clone().text()).trim();
        if (text && text.length < 300) return text;
      } catch { /* body already consumed or not readable */ }
    }
    return `${fallback} (server responded ${error.status})`;
  }
  if (error instanceof Error && error.message) return `${fallback} (${error.message})`;
  return fallback;
};

export const uploadFiles = async (files: File[]): Promise<{ name: string; url: string }[]> => {
  const response: { name: string; url: string }[] = [];
  for (const file of files) {
    try {
      const { uri, uploadUrl } = await askIOTApiFetch(
        `${getPublicApiBaseUrl()}/private/chatbot/sas-token`,
        { name: replaceSpaceWithHyphen(file.name), type: 'vendor' },
        'POST',
      );
      const uploadedUri = await uploadToStorage(uri, uploadUrl, file);

      response.push({ name: file.name, url: uploadedUri });
    } catch (error: unknown) {
      // #5012: name the file and the real cause. Returning [] here is what made every
      // storage, network and auth failure indistinguishable from a duplicate.
      const detail = await describeApiError(error, 'could not be uploaded');
      throw new Error(`"${file.name}" ${detail}`);
    }
  }
  return response;
};

const updateProfilePic = async (profilePicUrl: string) => {
  const response = await askIOTApiFetch(
    `${getPublicApiBaseUrl()}/private/users/profile-pic`,
    { profilePicUrl },
    'PUT',
  );
  cachedProfileAvatarUrl = profilePicUrl ?? null;
  return response;
};

export interface IProcessUploadsResult {
  uploaded: { name: string; file_id?: number }[];
  failed: { name: string; reason: string; duplicate?: boolean }[];
}

/**
 * Register uploaded documents against the bot for training.
 *
 * #5012: throws with the server's own per-file reason instead of returning false, and
 * returns the partial result so a batch where only some files were duplicates reports
 * exactly that rather than "everything failed".
 */
export const processUploads = async (
  files: IFile[],
  chat_token: string,
): Promise<IProcessUploadsResult> => {
  try {
    const result = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/training/process-uploads`,
      {
        files: files,
        chat_token,
      },
      'POST',
    );

    return {
      uploaded: result?.uploaded ?? [],
      failed: result?.failed ?? [],
    };
  } catch (error: unknown) {
    throw new Error(await describeApiError(error, 'Training registration failed.'));
  }
};

const uploadToStorage = async (uri: string, uploadUrl: string, file: File) => {
  try {
    const response = await storageAxios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });

    if (response.status === 200 || response.status === 201) {
      return uri;
    } else {
      throw new Error(`Failed to upload file. Status code: ${response.status}`);
    }
  } catch (error: unknown) {
    appLogger.error(`Error uploading file "${file.name}":`, {
      data: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const createBot = async (botName: string, isInternal = false) => {
  try {
    const res = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/chatbot/create`,
      {
        name: botName,
        isInternal,
      },
      'POST',
    );
    // ********** //
    return res;
  } catch {
    return null;
  }
};

/**
 * Delete a chatbot with its files, links and embeddings.
 *
 * The endpoint answers **204** on success, and `askIOTApiFetch` throws every status but
 * 200/201 — so a successful delete used to arrive here as a thrown Response and was
 * reported as a failure. A 204 is the success case; anything else becomes an Error whose
 * message is the server's own, so the caller can show it instead of failing silently.
 *
 * @param token - The bot's chat token.
 * @returns Resolves when the bot is gone.
 */
export const deleteBot = async (token: string): Promise<void> => {
  const url = `${getPublicApiBaseUrl()}/private/chatbot/delete-bot/${encodeURIComponent(token)}`;
  try {
    await askIOTApiFetch(url, {}, 'DELETE');
  } catch (error) {
    const status = (error as { status?: unknown } | null)?.status;
    if (status === 204) return;
    appLogger.error('Error in deleteBot:', { data: error });
    let message = 'The bot could not be deleted. Please try again.';
    if (status === 403) message = 'Deleting a bot needs an Enterprise plan or admin access to this workspace.';
    if (status === 404) message = 'This bot no longer exists. Refresh the page to update the list.';
    const response = error as { json?: () => Promise<{ error?: string; message?: string }> } | null;
    if (status !== 403 && status !== 404 && typeof response?.json === 'function') {
      const body = await response.json().catch(() => null);
      if (body?.message || body?.error) message = String(body.message || body.error);
    }
    throw new Error(message);
  }
};

export const updateBot = async (data: BotConfigPayload, chat_token: string) => {
  try {
    const res = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/chatbot/update`,
      {
        data: data,
        chat_token,
      },
      'POST',
    );
    // ********** //
    return res;
  } catch {
    return null;
  }
};

export const updateBotSetting = async (
  data: BotConfigPayload,
  chat_token: string,
  isDefault: boolean,
  routes: string[],
  supportURL: boolean,
  supportUrlValue: string,
  emailRequestIntensity: EmailRequestIntensity,
  requireEmailForChat: boolean,
  showFile?: boolean,
  enableFollowUpSuggestions?: boolean,
  showViewMore?: boolean,
  autoNavigateEnabled?: boolean,
  disallowFreeEmail?: boolean,
  suppressUrls?: string[],
  aiSuggestionsEnabled?: boolean,
  // mb-server#6239. An empty array is a meaningful value — it is how the operator turns the
  // country restriction back off — so this is sent whenever the form has hydrated, and the
  // backend treats [] as "show everywhere".
  allowedCountries?: string[],
) => {
  // Callers must see save failures: this used to catch and return null, so a
  // rejected save (including disallowFreeEmail — QX's "Block personal email
  // providers" setting) rendered "Settings saved successfully!" regardless of
  // whether it actually persisted. See Setting.tsx, which now surfaces the
  // rejection through its own catch block instead of always succeeding.
  return askIOTApiFetch(
    `${getPublicApiBaseUrl()}/private/chatbot/update`,
    {
      data: data,
      chat_token,
      isDefault,
      // A default bot shows on every page, and the backend rejects a default bot that
      // still has page routes (mb-server#4310). Settings hides the route list when the
      // default toggle is on but keeps it in state, so turning default on for a bot that
      // had routes failed every save with a 400 (Kajeet, 2026-10-05).
      routes: isDefault ? [] : routes,
      isSupport: supportURL,
      supportLink: supportUrlValue,
      emailRequestIntensity,
      requireEmailForChat,
      showFile,
      enableFollowUpSuggestions,
      showViewMore,
      autoNavigateEnabled,
      disallowFreeEmail,
      suppressUrls,
      aiSuggestionsEnabled,
      allowedCountries,
    },
    'POST',
  );
};

// Lead delivery destinations (#2851). Vendor-level: where this workspace's chatbot leads
// are sent. Email is always-on; HubSpot/Salesforce are toggles (only those the vendor has
// connected can be enabled). Persisted to lead_delivery_preferences.
export interface DeliveryPreferences {
  email: boolean;
  hubspot: boolean;
  salesforce: boolean;
}

export const getDeliveryPreferences = async (): Promise<DeliveryPreferences | null> => {
  try {
    const res = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/chatbot/delivery-preferences`,
    );
    return res?.deliveryPreferences ?? null;
  } catch {
    return null;
  }
};

export const saveDeliveryPreferences = async (
  prefs: { hubspot: boolean; salesforce: boolean },
): Promise<DeliveryPreferences | null> => {
  try {
    const res = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/chatbot/delivery-preferences`,
      { hubspot: prefs.hubspot, salesforce: prefs.salesforce },
      'PUT',
    );
    return res?.deliveryPreferences ?? null;
  } catch {
    return null;
  }
};

export const liveBot = async (chat_token: string) => {
  try {
    const res = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/chatbot/live`,
      { chat_token },
      'POST',
    );
    return res;
  } catch {
    return null;
  }
};

export const getTrainedData = async (chat_token: string) => {
  try {
    const trainedData = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/training/get?chat_token=${chat_token}`,
    );
    return trainedData.data;
  } catch {
    return null;
  }
};

export const getVendorId = async () => {
  try {
    const response = await apiInstance({
      method: 'GET',
      url: '/private/chatbot/vendor-id',
    });
    return response.data;
  } catch {
    return null;
  }
};

export const getLeadTimelines = async (chat_token: string) => {
  try {
    const response = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/vendors/get-buyer-journey/${chat_token}`,
    );
    return response;
  } catch {
    return null;
  }
};

export const fetchLinks = async (url: string, urlType: number) => {
  // 0 : Website 1 : SiteMap
  try {
    const fetchlinks = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/links/get-link?link=${url}&type=${
        urlType === 1 ? 'sitemap' : 'website'
      }`,
    );

    const arr: ILink[] = [];
    let index = 0;
    for (const link of fetchlinks) {
      arr.push({
        no: index++,
        url: link,
      });
    }
    return arr;
  } catch {
    return [];
  }
};

export const getLinks = async (token: string) => {
  // 0 : Website 1 : SiteMap
  try {
    const links = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/links/list-links?chat_token=${token}`,
    );
    const arr = [];
    for (const link of links) {
      arr.push({
        no: link.link_id,
        url: link.link,
        processed: link.processed, // Add the processed field
        created_at: link.created_at, // You might also want to include the created_at field if needed
      });
    }
    return arr;
  } catch {
    return null;
  }
};

export const addLink = async (links: string[], chat_token: string) => {
  try {
    await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/links/add-link`, { links, chat_token }, 'POST');
    return true;
  } catch {
    return null;
  }
};

export const authSign = async () => {
  try {
    const response = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/integration/signin`);

    // Log the entire response object to debug

    // Since the response is already a JSON object, you can directly use it
    const data = response;

    // Log the data received from the response

    if (data.authUrl) {
      window.location.href = data.authUrl;
    } else {
      throw new Error('No authUrl received');
    }

    return true;
  } catch (_error) {
    appLogger.error('Error initiating sign-in:', { data: _error });
    return null;
  }
};

export const googleAuthSign = async () => {
  try {
    const response = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/integration/google-signin`,
      {},
      'POST',
    );

    // Log the entire response object to debug
    appLogger.info('Response object:', { data: response });

    // Since the response is already a JSON object, you can directly use it
    const data = response;

    // Log the data received from the response

    if (data.authUrl) {
      window.location.href = data.authUrl;
    } else {
      throw new Error('No authUrl received');
    }

    return true;
  } catch (_error) {
    appLogger.error('Error initiating sign-in:', { data: _error });
    return null;
  }
};

export const gmailAuthSignIn = async () => {
  try {
    const data = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/integration/gmail-signin`);

    if (data.authUrl) {
      window.location.href = data.authUrl;
    } else {
      throw new Error('No authUrl received');
    }

    return true;
  } catch (error: any) {
    // askIOTApiFetch throws the raw Response on non-200 — parse it for a user-friendly message
    if (error instanceof Response) {
      const body = await error.json().catch(() => ({}));
      if (body.code === 'MAILBOX_LIMIT') {
        throw new Error(`Mailbox limit reached (${body.current}/${body.limit}). Upgrade your plan or remove an existing mailbox.`);
      }
    }
    appLogger.error('Error initiating Gmail sign-in:', { data: error });
    throw new Error('Failed to connect Gmail. Please try again.');
  }
};

export const outlookAuthSignIn = async () => {
  try {
    const data = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/integration/microsoft-email-signin`);
    if (data.authUrl) {
      window.location.href = data.authUrl;
    } else {
      throw new Error('No authUrl received from outlookAuthSignIn');
    }
    return true;
  } catch (error) {
    handleShowError(error);
    return false;
  }
};

export const removeLink = async (row: number) => {
  try {
    await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/links/delete-link/${row}`, {}, 'DELETE');
    return true;
  } catch {
    return null;
  }
};

export const updateIntegrationStatus = async (enabled: boolean, id: string, platform?: string) => {
  try {
    const response = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/integration/update-integration-status`,
      {
        authId: id,
        enabledState: enabled,
        ...(platform && { platform }),
      },
      `POST`,
    );
    return response; // data is the array of personas
  } catch (e) {
    appLogger.error('Failed to update integration status', {
      error: e,
      integrationId: id,
      enabled,
    });
    return [];
  }
};

export const addPersona = async (name: string, description: string, chatToken: string) => {
  try {
    const response = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/chatbot/personas`, {
      name,
      description,
      chatToken,
    });
    return response; // Assuming the response data is the added persona
  } catch (e) {
    appLogger.error('Failed to add persona', { error: e, chatToken, personaName: name });
    return null;
  }
};

// Callers must see save failures: this used to catch and return null, so a
// rejected save (auth expiry, validation error, network drop) rendered no
// differently than success, and the caller's own catch block never fired.
// See Persona.tsx's onHandleSavePersona, which now surfaces the rejection.
export const submitPersona = async (
  personaId: number,
  chatToken: string,
  gptVersion: number | null,
  advancedSettings?: PersonaAdvancedSettings,
) => {
  const payload = {
    personaId,
    chatToken,
    gptVersion,
    ...advancedSettings,
  };

  return askIOTApiFetch(`${getPublicApiBaseUrl()}/private/chatbot/personas/select`, payload);
};


export const getAllBotsData = async (vendorId: number) => {
  try {
    const res = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/chatbot/${vendorId}`);
    return res;
  } catch {
    return null;
  }
};

export const getWorkspaceTrackingToken = async (): Promise<{ token: string } | null> => {
  try {
    const res = await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/chatbot/tracking-token`);
    return res as { token: string };
  } catch {
    return null;
  }
};

export const enableSlack = async (data: {
  teamId: string;
  chatToken?: string; // Deprecated: integrations are now vendor-level, chatToken is optional
  availability: string;
  responseTimeout: number;
}) => {
  try {
    await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/integration/enable-slack`, { ...data }, 'POST');
    return true;
  } catch (_error) {
    // Check if the error is a network error or a response from the API
    if (_error instanceof Response) {
      // If it's a response from the API, parse the JSON error message
      const errorMessage = await _error.json();
      return errorMessage.error;
    } else {
      // If it's a network error or other type of error, return the error object
      return _error;
    }
  }
};

export type EnableTeamsResult = true | { code?: string; message: string };

export const enableTeams = async (data: {
  tenantId: string;
  chatToken?: string; // Deprecated: integrations are now vendor-level, chatToken is optional
  availability: string;
  responseTimeout: number;
}): Promise<EnableTeamsResult> => {
  try {
    await askIOTApiFetch(`${getPublicApiBaseUrl()}/private/integration/enable-teams`, { ...data }, 'POST');
    return true;
  } catch (_error) {
    // On failure return a normalized error object the modal can branch on:
    // `code` (e.g. 'NO_TEAMS_INSTALLATION') drives inline install-first guidance,
    // `message` is the human-readable text.
    if (_error instanceof Response) {
      try {
        const body = await _error.json();
        return {
          code: body?.code as string | undefined,
          message: (body?.error as string) || 'Failed to configure Microsoft Teams',
        };
      } catch {
        return { message: 'Failed to configure Microsoft Teams' };
      }
    }
    // Workspace switches abort in-flight requests — don't surface that as a real failure.
    if (_error instanceof DOMException && _error.name === 'AbortError') {
      return { code: 'ABORTED', message: '' };
    }
    return { message: 'Something went wrong configuring Microsoft Teams. Please try again.' };
  }
};

export const updateRefreshIntervalByPattern = async (
  urlPattern: string,
  refreshIntervalDays: number,
  chatToken: string,
) => {
  try {
    const cleanPattern = urlPattern.replace(/^\/+|\/+$/g, '');
    const payload = {
      url_pattern: cleanPattern,
      refresh_interval_days: refreshIntervalDays,
      chat_token: chatToken,
    };
    const response = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/links/refresh-interval/pattern`,
      payload, // Payload as second argument
      'POST', // Method as third argument
    );
    return response;
  } catch (error) {
    appLogger.error('Error updating refresh interval:', { data: error });
    throw error;
  }
};

export const updateAllRefreshIntervals = async (refreshIntervalDays: number, chatToken: string) => {
  try {
    const payload = {
      refresh_interval_days: refreshIntervalDays,
      chat_token: chatToken,
    };
    const response = await askIOTApiFetch(
      `${getPublicApiBaseUrl()}/private/links/refresh-interval/all`,
      payload, // Payload as second argument
      'POST', // Method as third argument
    );
    return response;
  } catch (error) {
    appLogger.error('Error updating all refresh intervals:', { data: error });
    throw error;
  }
};
